import { addDays, endOfDay, isAfter } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  rootId,
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';
import { ID } from 'shared/types/id';
import { DayPartSections } from 'shared/types/page-sections';
import { SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';
import { getDueTaskPlanning } from 'shared/utils/get-task-planning';
import { isNumber } from 'shared/utils/is-number';

export type Options = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
  hideNewTaskRow?: boolean;
};

const getTaskTimeslot = (
  task: Task,
  today: Timestamp,
  plannedOccurrence:
    | {
        date: Timestamp;
        timeslot?: {
          startTime?: number | null;
          duration?: number | null;
        } | null;
      }
    | undefined,
):
  | { startTime?: number | null; duration?: number | null }
  | null
  | undefined => {
  if (plannedOccurrence?.timeslot) {
    return plannedOccurrence.timeslot;
  }

  const schedule = getActiveSchedule(task.schedules, today);
  if (schedule?.timeslot) {
    return schedule.timeslot;
  }

  return task.timeslot;
};

const getDayPartSection = (
  timeslot:
    | { startTime?: number | null; duration?: number | null }
    | null
    | undefined,
): DayPartSections => {
  if (!isNumber(timeslot?.startTime)) {
    return DayPartSections.AllDay;
  }

  const date = new Date();
  date.setHours(0, 0, 0, timeslot!.startTime!);
  const startHour = date.getHours();

  if (startHour < 12) {
    return DayPartSections.Morning;
  }

  if (startHour >= 12 && startHour < 18) {
    return DayPartSections.Afternoon;
  }

  return DayPartSections.Evening;
};

export const useDayPartTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
  hideNewTaskRow,
}: Options) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const overdueDeadline = endOfDay(addDays(today, -1));
    const dayPartTreeItems: Record<ID, TreeItem> = {
      [DayPartSections.Overdue]: {
        id: DayPartSections.Overdue,
        type: TreeItemTypes.DayPart,
        name: t('general.sections.overdue.title'),
        children: [],
        isFolder: true,
      },
      [DayPartSections.AllDay]: {
        id: DayPartSections.AllDay,
        type: TreeItemTypes.DayPart,
        name: t('general.sections.day-part.allDay.title'),
        children: [],
        isFolder: true,
      },
      [DayPartSections.Morning]: {
        id: DayPartSections.Morning,
        type: TreeItemTypes.DayPart,
        name: t('general.sections.day-part.morning.title'),
        children: [],
        isFolder: true,
      },
      [DayPartSections.Afternoon]: {
        id: DayPartSections.Afternoon,
        type: TreeItemTypes.DayPart,
        name: t('general.sections.day-part.afternoon.title'),
        children: [],
        isFolder: true,
      },
      [DayPartSections.Evening]: {
        id: DayPartSections.Evening,
        type: TreeItemTypes.DayPart,
        name: t('general.sections.day-part.evening.title'),
        children: [],
        isFolder: true,
      },
    };
    const dayPartIds = [
      DayPartSections.Overdue,
      DayPartSections.AllDay,
      DayPartSections.Morning,
      DayPartSections.Afternoon,
      DayPartSections.Evening,
    ];

    const taskDayPartMap: Record<ID, DayPartSections> = {};
    const overdueTasks: Task[] = [];
    const standardTasksMap = new Map<ID, Task>();

    // Process each task to determine its dayPart section
    tasks.forEach((task) => {
      if (task.completedAt) {
        return;
      }

      // Check if task is overdue using same logic as useDeadlineTree
      const overduePlanning = getDueTaskPlanning(task, {
        date: overdueDeadline,
        today,
        weekStartsOn,
      });

      const duePlanning = overduePlanning.filter(
        (plannedOccurrence) =>
          !isAfter(plannedOccurrence.date, overdueDeadline),
      );

      const plannedOccurrence = duePlanning[duePlanning.length - 1];
      const isOverdue =
        !!plannedOccurrence ||
        (task.deadlineDeadline &&
          !isAfter(task.deadlineDeadline, overdueDeadline));

      // If task is overdue, add to overdue section
      if (isOverdue) {
        taskDayPartMap[task.id] = DayPartSections.Overdue;
        overdueTasks.push(task);
        return;
      }

      // Get first upcoming/overdue planned occurrence
      const planning = getDueTaskPlanning(task, {
        date: today,
        today,
        weekStartsOn,
      });

      const firstOccurrence = planning[0];
      if (!firstOccurrence) {
        // Task has no planning, assign to AllDay section based on task timeslot
        const timeslot = task.timeslot;
        taskDayPartMap[task.id] = getDayPartSection(timeslot);
        standardTasksMap.set(task.id, task);
        return;
      }

      // Get timeslot from first occurrence
      const timeslot = getTaskTimeslot(task, today, firstOccurrence);
      taskDayPartMap[task.id] = getDayPartSection(timeslot);
      standardTasksMap.set(task.id, task);
    });

    // Get custom order for all sections
    const customTaskOrder =
      sortingMode === SortingMode.Custom
        ? dayPartIds.reduce<ID[]>((acc, dayPartId) => {
            const sectionOrder = customOrder?.[dayPartId] ?? [];
            acc.push(...sectionOrder);
            return acc;
          }, [])
        : [];

    // Sort tasks and assign to sections

    // Sort overdue tasks
    const overdueSortedTasks = getSortedTasks(overdueTasks, {
      today,
      weekStartsOn,
      order: customTaskOrder,
      mode: [SortingMode.DueDate],
      allDayFirst: true,
      invertedTimeslots: true,
    });

    // Populate overdue section
    overdueSortedTasks.forEach((task) => {
      const dayPartTreeItem = dayPartTreeItems[DayPartSections.Overdue];
      if (dayPartTreeItem) {
        dayPartTreeItem.children?.push(task.id);
      }

      // Add task tree item
      dayPartTreeItems[task.id] = {
        id: task.id,
        name: task.name,
        type: TreeItemTypes.Task,
      };
    });

    // Sort standard tasks
    const standardSortedTasks = getSortedTasks(
      Array.from(standardTasksMap.values()),
      {
        today,
        weekStartsOn,
        order: customTaskOrder,
        mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
      },
    );

    // Populate standard sections
    const allTreeItems = standardSortedTasks.reduce<Record<ID, TreeItem>>(
      (acc, task) => {
        const dayPart = taskDayPartMap[task.id];
        if (!dayPart || dayPart === DayPartSections.Overdue) {
          return acc;
        }

        const dayPartTreeItem = acc[dayPart];
        if (dayPartTreeItem) {
          dayPartTreeItem.children?.push(task.id);
        }

        // Add task tree item
        acc[task.id] = {
          id: task.id,
          name: task.name,
          type: TreeItemTypes.Task,
        };

        return acc;
      },
      dayPartTreeItems,
    );

    // Add New Task row to each section
    if (!hideNewTaskRow) {
      dayPartIds.forEach((dayPartId) => {
        if (dayPartId === DayPartSections.Overdue) {
          return;
        }

        const dayPartTreeItem = allTreeItems[dayPartId];
        if (dayPartTreeItem) {
          const newTaskItemId = `${dayPartId}-new-task`;
          dayPartTreeItem.children?.push(newTaskItemId);
          allTreeItems[newTaskItemId] = {
            id: newTaskItemId,
            name: 'New Task',
            type: TreeItemTypes.NewTask,
          };
        }
      });
    }

    // Build section IDs in order: Overdue, AllDay, Morning, Afternoon, Evening
    const sectionIds: ID[] = dayPartIds.filter((dayPartId) => {
      const treeItem = allTreeItems[dayPartId];
      return (
        treeItem &&
        treeItem.children &&
        (showEmptyGroups || treeItem.children.length)
      );
    });

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TreeItemTypes.Root,
        children: sectionIds,
      },
      ...allTreeItems,
    };
  }, [
    customOrder,
    sortingMode,
    t,
    tasks,
    today,
    weekStartsOn,
    showEmptyGroups,
    hideNewTaskRow,
  ]);
};
